import { Router, watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import { bound } from "@frui.ts/helpers";
import { computed, observable, reaction } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import { Modules, newNavigationName } from "config";
import OutageRepository from "repositories/outageRepository";
import SystemComponent from "entities/systemComponent";
import OptionsService from "services/optionsService";
import CreateViewModel from "../../createViewModel";
import { getOutageMessage } from "./utils";
import OutageState from "entities/outageState";
import SystemComponentCode from "entities/systemComponentCode";
import { SystemComponentOutage } from "entities/systemComponentOutageRequest";
import { OutageValidationRules } from "entities/systemComponentOutageRequest";

@Router.registerRoute({ name: Router.Self, route: newNavigationName })
export default class OutageCreateViewModel extends CreateViewModel<SystemComponentOutage> {
  readonly modelName = "outage";
  @observable outageState: OutageState = OutageState.ACTIVE;
  private unsubscriptions: Array<() => void> = [];

  @computed
  get outageMessage() {
    if (this.item) {
      const message = this.translate(
        `messages.${this.systemComponentCode}.${this.outageState}.${this.item.validUntil ? "validUntil" : "validInfinity"}`
      );
      return getOutageMessage(this.item, message);
    }
    return undefined;
  }

  @computed
  get impact() {
    return this.translate(`messages.${this.systemComponentCode}.impact.${this.outageState}`);
  }

  @computed
  get systemComponentCode(): SystemComponentCode {
    const code: SystemComponentCode | string | undefined = this.optionsService
      .optionsViewModel(SystemComponent)
      .items.find(item => item.id === this.item?.systemComponentId)?.code;

    if (code === undefined || !Object.values(SystemComponentCode).includes(code as SystemComponentCode)) {
      return SystemComponentCode.OTHER;
    } else {
      return code as SystemComponentCode;
    }
  }

  constructor(
    protected repository: OutageRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    private optionsService: OptionsService
  ) {
    super();
    this.navigationName = newNavigationName;
    this.unsubscriptions.push(
      reaction(
        () => [
          this.item?.validSince,
          this.item?.validUntil,
          this.item?.visibleSince,
          this.item?.visibleUntil,
          this.item?.systemComponentId,
          this.item?.description,
          this.item?.systemComponentOutageLocalizations,
        ],
        this.onSystemComponentChange
      ),
      reaction(() => [this.item?._visibleSinceTime], this.setVisibleSince),
      reaction(() => [this.item?._validSinceTime], this.setValidSince),
      reaction(() => [this.item?._validUntilTime], this.setValidUntil)
    );
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.setSystemComponentCode();

    attachAutomaticValidator(this.item, OutageValidationRules);
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get systemComponentOptions() {
    return this.optionsService.entityOptions(SystemComponent);
  }

  protected loadDetail() {
    return new SystemComponentOutage();
  }

  @bound
  @watchBusy
  async create() {
    if (!validate(this.item)) {
      return;
    }
    this.setValidSince();
    this.setVisibleSince();
    this.setValidUntil();
    await super.create();
  }

  @bound
  setSystemComponentCode() {
    const code: SystemComponentCode | string | undefined = this.optionsService
      .optionsViewModel(SystemComponent)
      .items.find(item => item.id === this.item?.systemComponentId)?.code;

    if (code === undefined || !Object.values(SystemComponentCode).includes(code as SystemComponentCode)) {
      this.item._systemComponentCode = SystemComponentCode.OTHER;
    } else {
      this.item._systemComponentCode = code;
    }
  }

  @bound
  private setValidSince() {
    if (!this.item._validSinceTime || !this.item.validSince) {
      return false;
    }
    const [hours, minutes, seconds] = this.item._validSinceTime.split(":");
    const timeDate = new Date(this.item.validSince);
    timeDate.setHours(parseInt(hours, 10));
    timeDate.setMinutes(parseInt(minutes, 10));
    timeDate.setSeconds(parseInt(seconds, 10));
    this.item.validSince = timeDate;
    return true;
  }

  @bound
  private setVisibleSince() {
    if (!this.item._visibleSinceTime || !this.item.visibleSince) {
      return false;
    }
    const [hours, minutes, seconds] = this.item._visibleSinceTime.split(":");
    const timeDate = new Date(this.item.visibleSince);
    timeDate.setHours(parseInt(hours, 10));
    timeDate.setMinutes(parseInt(minutes, 10));
    timeDate.setSeconds(parseInt(seconds, 10));
    this.item.visibleSince = timeDate;
    return true;
  }

  @bound
  private setValidUntil() {
    if (!this.item._validUntilTime || !this.item.validUntil) {
      return false;
    }
    const [hours, minutes, seconds] = this.item._validUntilTime.split(":");
    const timeDate = new Date(this.item.validUntil);
    timeDate.setHours(parseInt(hours, 10));
    timeDate.setMinutes(parseInt(minutes, 10));
    timeDate.setSeconds(parseInt(seconds, 10));
    this.item.validUntil = timeDate;
    return true;
  }

  @bound
  onSystemComponentChange() {
    this.setSystemComponentCode();
    // If we have translated outages set, but the component is not Other, reset the translations
    // so the "description" field is used instead.
    if (
      this.item.systemComponentOutageLocalizations &&
      this.item.systemComponentOutageLocalizations.length > 0 &&
      this.systemComponentCode !== SystemComponentCode.OTHER
    ) {
      this.item.systemComponentOutageLocalizations = undefined;
    }
    // Reset description if we're switching to Other component
    if (this.item.description && this.item.description.length > 0 && this.systemComponentCode === SystemComponentCode.OTHER) {
      this.item.description = undefined;
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `outages.${code}`, size);
  }
}
